/**
 * Which pages escape the blur while an installation is on hold.
 *
 * Kept free of React and auth imports so it can be reasoned about — and tested — on its own: this
 * is the difference between a suspension that holds and one that either leaks the whole app or
 * traps a paying customer with no way to pay.
 */

/** Where the customer goes to fix it — deliberately left reachable behind the lock. */
export const BILLING_PATH = '/billing';
/** Where the vendor goes to lift it. */
export const BILLING_ADMIN_PATH = '/billing-admin';

/**
 * BOTH billing pages stay clear, for everyone.
 *
 * Not one-or-the-other by role: the same person is often the vendor admin who placed the hold AND
 * the one correcting the card, and a tenant's Billing Admin needs the payment surface itself.
 * Neither page is a way around the hold — /billing-admin refuses anyone outside the vendor
 * allowlist server-side, and /billing is where the money is meant to change hands.
 */
export const ACCESS_ESCAPE_ROUTES = [BILLING_PATH, BILLING_ADMIN_PATH];

/**
 * Exact-segment match. `startsWith('/billing')` also matches `/billing-admin`, which is fine now
 * that both escape, but was not when they differed — and a route named `/billings` never should.
 */
export function isOnRoute(pathname: string, route: string): boolean {
  return pathname === route || pathname.startsWith(`${route}/`);
}

/** True on a page that stays readable while the app is withheld. */
export function isAccessEscapeRoute(pathname: string): boolean {
  return ACCESS_ESCAPE_ROUTES.some((route) => isOnRoute(pathname, route));
}

/**
 * Pages that belong to the INSTALL or to the PERSON, not to a Brand Workspace.
 *
 * A page where a `?ws=` would be meaningless is also a page that cannot sensibly require a
 * workspace to exist. Site admins are the case that proves it: Cheng and Tim administer an install
 * on the customer's behalf and have no reason to own a Brand Workspace there — Brand Workspaces are
 * the customer's. Gating these routes on one locked them out of the very page where an install is
 * configured, behind a screen whose only action was to create a workspace they should not create.
 *
 * This is the second time that shape of bug has bitten: the payment lock's own "Go to Billing" used
 * to land a workspace-less admin on "Create your first Brand Workspace", whose only action is a
 * write the lock refuses. Hence one list, here, used by both the no-workspace gate
 * (`WorkspaceGate`) and the `?ws=` stripping (`TenantContext`) — rather than two that drift.
 */
export const WORKSPACE_AGNOSTIC_ROUTES = [
  '/site-integrations',
  '/billing',
  '/billing-admin',
  '/app-settings',
  '/profile',
];

/** True on an install-level or per-user page, which never requires a Brand Workspace. */
export function isWorkspaceAgnosticRoute(pathname: string): boolean {
  return WORKSPACE_AGNOSTIC_ROUTES.some((route) => isOnRoute(pathname, route));
}
